import { useCallback, type VoidFunctionComponent } from 'react';

import ModalAddDependency from '../../../modals/ModalAddDependency/ModalAddDependency';
import type { ManuallyAddedDependencyRequestKind } from '../../../../../typings/api';

/**
 * @param {string} userRevisionLocator - the user project; (aka parent revision)
 * @param {ManuallyAddedDependencyRequestKind | null} manualDependencyAddMode - the current add mode; (new/replace)
 * @param {(mode: ManuallyAddedDependencyRequestKind | null) => void} setManualDependencyAddMode - setter for add mode
 * @param {string | null} dependencyLocatorToOverwrite - optional dependency locator for replace mode
 * @param {(locator: string | null) => void} setDependencyLocatorToOverwrite - setter for optional dependency locator
 */
type Props = {
  userRevisionLocator: string;
  manualDependencyAddMode: ManuallyAddedDependencyRequestKind | null;
  setManualDependencyAddMode: (mode: ManuallyAddedDependencyRequestKind | null) => void;
  dependencyLocatorToOverwrite: string | null;
  setDependencyLocatorToOverwrite: (locator: string | null) => void;
  vendoredPath?: string;
};

const AddDependency: VoidFunctionComponent<Props> = ({
  userRevisionLocator,
  manualDependencyAddMode,
  setManualDependencyAddMode,
  dependencyLocatorToOverwrite,
  setDependencyLocatorToOverwrite,
  vendoredPath,
}) => {
  const onClose = useCallback(() => {
    // Reset the mode to close the modal - and the optional locator to clean up
    setManualDependencyAddMode(null);
    setDependencyLocatorToOverwrite(null);
  }, [setManualDependencyAddMode, setDependencyLocatorToOverwrite]);

  if (!manualDependencyAddMode) {
    return null;
  }

  return (
    <ModalAddDependency
      mode={manualDependencyAddMode}
      dependencyToOverwrite={dependencyLocatorToOverwrite ?? undefined}
      parentLocator={userRevisionLocator}
      show={manualDependencyAddMode != null}
      onHide={onClose}
      onProjectChange={onClose}
      vendoredPath={vendoredPath}
  />
  );
};

export default AddDependency;
